import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import EditPencil from '../../../../../assets/icons_new/EditPencilWhite.svg'
import Deactivate from '../../../../../assets/icons_new/DeActivateColor.svg'
import Activate from '../../../../../assets/icons_new/ActivateColor.svg'
import { VehicleModel } from '@/app/models'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { getTripVehicleTypeName } from '@/app/common/converters/newDelegation/tripVehicleTypeToTextConverter'
import { TripVehicleType } from '@/app/constants/delegation/tripVehicleType'
import useVehicleService from '../../../services/VehicleService'
import { toast } from '@/lib/toasts/api'

interface VehicleBasicInfoProps {
  vehicle: VehicleModel
  companyId: string
  vehicleId: string
  onRefresh: () => void
  setIsOpenEditVehicle: any
}

const VehicleBasicInfo = ({
  vehicle,
  companyId,
  vehicleId,
  onRefresh,
  setIsOpenEditVehicle,
}: VehicleBasicInfoProps) => {
  const { t } = useLocalize()
  const { activateVehicle } = useVehicleService()
  const isActive = vehicle?.isActive

  const formatMomentDate = (date?: Moment | string) => {
    if (!date) return '-'
    const m = moment.isMoment(date) ? date : moment(date)
    if (!m.isValid()) return '-'
    return m.format('DD.MM.YYYY')
  }

  const getVehicleTypeName = (vehicleType?: TripVehicleType) => {
    if (vehicleType) return '-'
    return getTripVehicleTypeName(vehicleType, t)
  }

  const handleActivationToggle = async () => {
    try {
      await activateVehicle({
        companyId,
        vehicleIds: [vehicleId],
        setActive: !isActive,
      })
      toast.success({
        text: isActive
          ? t('vehicleModule.vehicle-deactivated')
          : t('vehicleModule.vehicle-activated'),
      })
      onRefresh()
    } catch (err) {
      toast.error({
        text: isActive
          ? t('vehicleModule.error-deactivating-vehicle')
          : t('vehicleModule.error-activating-vehicle'),
      })
    }
  }

  return (
    <section className="tw-relative tw-flex tw-flex-col">
      {/* <div>AKTUALNE ID POJAZDU - {vehicleId} ORAZ {vehicle?.id}</div> */}
      <div className="tw-absolute tw-right-0 tw-top-6 tw-flex tw-gap-2">
        <Tooltip
          text={
            isActive
              ? t('vehicleModule.deactivate')
              : t('vehicleModule.activate')
          }
          className="tw-grid tw-cursor-pointer tw-rounded-lg tw-bg-secondary tw-p-2 hover:tw-opacity-90"
          onClick={handleActivationToggle}
        >
          <img
            src={isActive ? Deactivate : Activate}
            className="tw-h-4 tw-w-4"
            alt={isActive ? 'Deactivate' : 'Activate'}
          />
        </Tooltip>
        <Tooltip
          text="Edytuj"
          className="tw-grid tw-cursor-pointer tw-rounded-lg tw-bg-primary tw-p-2 hover:tw-opacity-90"
          onClick={() => setIsOpenEditVehicle(true)}
        >
          <img src={EditPencil} className="tw-h-4 tw-w-4" alt="Edit" />
        </Tooltip>
      </div>
      <div className="tw-flex tw-flex-col tw-gap-8 tw-py-5 tw-pt-6 lg:tw-flex-row">
        <div
          className="tw-flex tw-w-full tw-flex-col tw-gap-2.5 lg:tw-max-w-[450px]"
          data-openreplay-masked
        >
          <ActiveBlock isActive={isActive} onToggle={handleActivationToggle} />
          <InfoBlock
            label={t('vehicleBasics.registration-number')}
            lines={[vehicle?.registrationNumber || '-']}
          />
          <InfoBlock
            label={t('vehicleBasics.vin-number')}
            lines={[vehicle?.vinNumber || '-']}
          />
          <InfoBlock
            label={t('vehicleBasics.make')}
            lines={[vehicle?.make || '-']}
          />
          <InfoBlock
            label={t('vehicleBasics.model')}
            lines={[vehicle?.model || '-']}
          />
          <InfoBlock
            label={t('vehicleBasics.vehicle-type')}
            lines={[getVehicleTypeName(vehicle?.vehicleType)]}
          />
          <InfoBlock
            label={t('vehicleBasics.registration-number-from-tachograph')}
            lines={[vehicle?.registrationNumberFromTachograph || '-']}
          />
          <Divider />
          <InfoBlock
            label={t('vehicleBasics.purchase-date')}
            lines={[formatMomentDate(vehicle?.purchaseDate)]}
          />
          <InfoBlock
            label={t('vehicleBasics.sale-date')}
            lines={[formatMomentDate(vehicle?.saleDate)]}
          />
        </div>
        <div
          className="tw-flex tw-w-full tw-flex-col tw-gap-2.5 lg:tw-max-w-[450px]"
          data-openreplay-masked
        >
          <InfoBlock
            label={t('vehicleBasics.company')}
            lines={[vehicle?.company || '-']}
          />
          <InfoBlock
            label={t('vehicleBasics.phone-number')}
            lines={[vehicle?.phoneNumber || '-']}
          />
          <InfoBlock
            label={t('vehicleBasics.registration-country')}
            lines={[vehicle?.registrationCountry?.name || '-']}
          />
          <InfoBlock
            label={t('vehicleBasics.registry-number')}
            lines={[vehicle?.registryNumber || '-']}
          />
          <Divider />
          <InfoBlock
            label={t('vehicleBasics.insurance-validity-date')}
            lines={[formatMomentDate(vehicle?.insuranceValidityDate)]}
          />
          <InfoBlock
            label={t('vehicleBasics.checkup-date')}
            lines={[formatMomentDate(vehicle?.checkupDate)]}
          />
          <InfoBlock
            label={t('vehicleBasics.calibration-date')}
            lines={[formatMomentDate(vehicle?.calibrationDate)]}
          />
          <InfoBlock
            label={t('vehicleBasics.next-calibration-date')}
            lines={[formatMomentDate(vehicle?.nextCalibrationDate)]}
          />
          <Divider />
          <InfoBlock
            label={t('vehicleBasics.tachograph-manufacturer')}
            lines={[vehicle?.tachographManufacturer || '-']}
          />
          <InfoBlock
            label={t('vehicleBasics.tachograph-generation')}
            lines={[vehicle?.tachographGeneration || '-']}
          />
        </div>
      </div>
    </section>
  )
}

export default VehicleBasicInfo

import CheckmarkGreen from '../../../../../assets/icons_new/CheckmarkGreen.svg'
import Close from '../../../../../assets/icons_new/CloseGray.svg'
import Tooltip from '@/modules/drivers/shared/ToolTip'

// Sam status jest przełącznikiem: dezaktywacja pojedynczego pojazdu nie ma wtedy
// prowadzić przez modal edycji, tylko przez kliknięcie w to, co i tak się czyta.
const ActiveBlock = ({
  isActive,
  onToggle,
}: {
  isActive: boolean
  onToggle: () => void
}) => {
  const { t } = useLocalize()
  return (
    <div className="tw-text-sm">
      <label className="tw-mb-0 tw-py-0.5 tw-font-medium">
        {t('common.status')}
      </label>
      <div>
        <Tooltip
          align="start"
          text={
            isActive
              ? t('vehicleModule.deactivate')
              : t('vehicleModule.activate')
          }
          onClick={onToggle}
          className="tw--ml-1 tw-flex tw-items-center tw-rounded tw-px-1 hover:tw-bg-base-300"
        >
          {isActive ? (
            <>
              <img src={CheckmarkGreen} alt="Checkmark Icon" />
              <span className="tw-text-sm tw-font-semibold tw-text-success">
                {t('common.active')}
              </span>
            </>
          ) : (
            <>
              <img src={Close} alt="Close Icon" />
              <span className="tw-text-sm tw-font-semibold tw-text-content-base/70">
                {t('common.inactive')}
              </span>
            </>
          )}
        </Tooltip>
      </div>
    </div>
  )
}

const InfoBlock = ({ label, lines, withTranslation = false }) => {
  const { t } = useLocalize()
  return (
    <>
      <div className="tw-text-sm">
        <label className="tw-mb-0 tw-py-0.5 tw-font-medium">{label}</label>
        <div>
          {lines.map((line: string | string[], i) => (
            <div key={i}>
              {Array.isArray(line)
                ? line.join('')
                : withTranslation
                  ? t(line)
                  : (line ?? '-')}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

const Divider = () => {
  return <div className="tw-h-[1px] tw-w-full tw-bg-base-300"></div>
}
